import {
  PreScreeningQuestion,
  QuestionType,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  CARDIOVASCULAR_ATTRIBUTES,
  MEASUREMENT_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
  HORMONE_ATTRIBUTES,
  CANCER_ATTRIBUTES,
  DIABETES_ATTRIBUTES,
  IMMUNOSUPPRESSION_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for AZASCVD Established Cardiovascular Disease Study - Cohort A
 * For adults who have a history of atherosclerotic cardiovascular disease (ASCVD)
 * such as heart attacks or strokes
 */
export const azascvdEstablishedScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "age_range",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      { value: "under_18", label: "Under 18", disqualify: true },
      { value: "18_64", label: "18-64" },
      { value: "65_plus", label: "65 and older" },
    ],
    disqualifyMessage: "This study is only for participants who are 18 years of age or older.",
    attributeMapping: mappers.value({
      under_18: [],
      "18_64": [],
      "65_plus": [],
    }),
  },

  {
    id: "cardiovascular_history",
    type: QuestionType.CHECKBOX,
    question: "Have any of the following happened to you?",
    helpText: "This study is specifically for people with established cardiovascular disease. Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "heart_attack_12mo",
        label: "I have had a heart attack in the past 12 months",
      },
      {
        value: "stroke_12mo",
        label: "I have had a stroke in the past 12 months",
      },
      {
        value: "peripheral_revascularization",
        label: "I have had revascularization (stenting or bypass surgery) for leg artery narrowing at any time in the past",
      },
      {
        value: "none",
        label: "None of the above applies to me",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study requires participants to have had a heart attack or stroke in the past 12 months, OR revascularization for symptomatic lower limb PAD at any time.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        heart_attack_12mo: [
          { attributeDefinitionId: 369, booleanAttributeValue: true },
        ],
        stroke_12mo: [
          { attributeDefinitionId: 20664, booleanAttributeValue: true },
        ],
        peripheral_revascularization: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PERIPHERAL_ARTERY_DISEASE, booleanAttributeValue: true },
        ],
      },
      noneValue: "none",
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: 369, booleanAttributeValue: false },
          { attributeDefinitionId: 20664, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PERIPHERAL_ARTERY_DISEASE, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "ldl_cholesterol_check",
    type: QuestionType.RADIO,
    question: "Have you had your LDL cholesterol checked in the past 6 weeks?",
    helpText: "LDL or low density lipoprotein is one of the subtypes of cholesterol, sometimes called 'bad' cholesterol",
    required: true,
    allowBack: true,
    options: [
      {
        value: "not_tested",
        label: "I have not had my LDL tested in the past 6 weeks",
      },
      {
        value: "cannot_recall",
        label: "I cannot recall my most recent result",
      },
      {
        value: "under_60",
        label: "My recent LDL was <60",
        disqualify: true,
      },
      {
        value: "60_to_74",
        label: "My recent LDL was 60–74",
      },
      {
        value: "75_to_99",
        label: "My recent LDL was 75–99",
      },
      {
        value: "100_or_greater",
        label: "My recent LDL was 100 or greater",
      },
    ],
    disqualifyMessage: "This study requires participants who either haven't had recent LDL testing or have LDL levels of 60 or higher.",
    attributeMapping: mappers.value({
      not_tested: [
        { attributeDefinitionId: 26821, radioAttributeValue: "000-6t95z9" },
      ],
      cannot_recall: [
        { attributeDefinitionId: 26821, radioAttributeValue: "001-k5vwt" },
      ],
      under_60: [
        { attributeDefinitionId: 26821, radioAttributeValue: "002-under60" },
      ],
      "60_to_74": [
        { attributeDefinitionId: 26821, radioAttributeValue: "003-prioml" },
      ],
      "75_to_99": [
        { attributeDefinitionId: 26821, radioAttributeValue: "004-75to99" },
      ],
      "100_or_greater": [
        { attributeDefinitionId: 26821, radioAttributeValue: "005-sd2mij" },
      ],
    }),
  },

  {
    id: "additional_risk_factors",
    type: QuestionType.CHECKBOX,
    question: "Do any of the following apply to you?",
    helpText: "Select all that apply. You need at least one of these to qualify for the study.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "diabetes_medication",
        label: "I have type 2 diabetes and take medication for it",
      },
      {
        value: "multiple_vascular_beds",
        label: "I have cardiovascular disease affecting multiple areas (heart, brain, and/or leg arteries)",
      },
      {
        value: "recurrent_events",
        label: "I have had multiple cardiovascular events (multiple heart attacks or strokes)",
      },
      {
        value: "amputation_pad",
        label: "I have had an amputation above the ankle due to leg artery narrowing",
      },
      {
        value: "chronic_kidney_disease",
        label: "I have been diagnosed with chronic kidney disease",
      },
      {
        value: "none",
        label: "None of the above applies to me",
        disqualify: (allAnswers) => {
          const ageRange = allAnswers?.["age_range"];
          // Auto-pass if 65+, otherwise disqualify if no risk factors
          return ageRange !== "65_plus";
        },
      },
    ],
    disqualifyMessage: "This study requires participants to have additional risk factors beyond their cardiovascular history.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        diabetes_medication: [
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: true },
        ],
        multiple_vascular_beds: [],
        recurrent_events: [],
        amputation_pad: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PERIPHERAL_ARTERY_DISEASE, booleanAttributeValue: true },
        ],
        chronic_kidney_disease: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: true },
        ],
      },
      noneValue: "none",
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "high_intensity_statin",
    type: QuestionType.RADIO,
    question: "Are you currently taking either Atorvastatin (Lipitor) 40 mg or higher, or Rosuvastatin (Crestor) 20 mg or higher to manage high cholesterol?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I am taking one of these high-intensity statins",
      },
      {
        value: "no",
        label: "No, I am not taking these high-intensity statins",
        disqualify: true,
      },
    ],
    disqualifyMessage: "This study requires participants to be taking high-intensity statins (Atorvastatin 40+ mg or Rosuvastatin 20+ mg).",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 26769, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: 26769, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "major_medical_conditions",
    type: QuestionType.CHECKBOX,
    question: "Do any of the following apply to you?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "cancer_recent",
        label: "I have had a cancer diagnosis or treatment within the past 5 years (excluding SCC, BCC, and in-situ cervical cancer)",
        disqualify: true,
      },
      {
        value: "organ_transplant",
        label: "I have had a major organ transplant",
        disqualify: true,
      },
      {
        value: "uncontrolled_diabetes",
        label: "I have uncontrolled diabetes (HbA1c ≥ 9.5%)",
        disqualify: true,
      },
      {
        value: "severe_heart_failure",
        label: "I have severe heart failure",
        disqualify: true,
      },
      {
        value: "end_stage_kidney_disease",
        label: "I have end stage kidney failure or I am on dialysis",
        disqualify: true,
      },
      {
        value: "liver_failure_cirrhosis",
        label: "I have liver failure or cirrhosis",
        disqualify: true,
      },
      {
        value: "hepatitis_b_c",
        label: "I have hepatitis B or hepatitis C",
        disqualify: true,
      },
      {
        value: "pregnancy_breastfeeding",
        label: "I am currently pregnant, breastfeeding, or planning to become pregnant",
        disqualify: true,
      },
      {
        value: "none",
        label: "None of the above is true for me",
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants with these medical conditions.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        cancer_recent: [
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: true },
        ],
        organ_transplant: [
          { attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: true },
        ],
        uncontrolled_diabetes: [
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HBA1C_MORE_THAN_10, booleanAttributeValue: true },
        ],
        severe_heart_failure: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: true },
        ],
        pregnancy_breastfeeding: [
          { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: true },
        ],
        end_stage_kidney_disease: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: true },
        ],
        liver_failure_cirrhosis: [
          { attributeDefinitionId: 72, booleanAttributeValue: true },
        ],
        hepatitis_b_c: [
          { attributeDefinitionId: 834, booleanAttributeValue: true },
          { attributeDefinitionId: 15040, booleanAttributeValue: true },
        ],
      },
      noneValue: "none",
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: CANCER_ATTRIBUTES.HAS_CANCER, booleanAttributeValue: false },
          { attributeDefinitionId: IMMUNOSUPPRESSION_ATTRIBUTES.HAS_ORGAN_TRANSPLANT, booleanAttributeValue: false },
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HBA1C_MORE_THAN_10, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: false },
          { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: false },
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: false },
          { attributeDefinitionId: 72, booleanAttributeValue: false },
          { attributeDefinitionId: 834, booleanAttributeValue: false },
          { attributeDefinitionId: 15040, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "additional_excluded_conditions",
    type: QuestionType.CHECKBOX,
    question: "Do any of the following apply to you?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "long_qt_syndrome",
        label: "I have long QT syndrome or others in my family have long QT syndrome",
        disqualify: true,
      },
      {
        value: "ventricular_arrhythmias",
        label: "I have been diagnosed with a ventricular arrhythmia (such as ventricular tachycardia or ventricular fibrillation) that requires ongoing treatment",
        disqualify: true,
      },
      {
        value: "homozygous_familial_hypercholesterolemia",
        label: "I have been diagnosed with homozygous familial hypercholesterolemia",
        disqualify: true,
      },
      {
        value: "cardiomyopathy",
        label: "I have been diagnosed with cardiomyopathy (including hypertrophic cardiomyopathy) or infiltrative cardiomyopathies caused by sarcoidosis or amyloidosis",
        disqualify: true,
      },
      {
        value: "none",
        label: "None of the above is true for me",
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants with these medical conditions or histories.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        long_qt_syndrome: [
          { attributeDefinitionId: 23503, booleanAttributeValue: true },
        ],
        ventricular_arrhythmias: [
          { attributeDefinitionId: 26744, booleanAttributeValue: true },
        ],
        homozygous_familial_hypercholesterolemia: [
          { attributeDefinitionId: 23750, booleanAttributeValue: true },
        ],
        cardiomyopathy: [
          { attributeDefinitionId: 23518, booleanAttributeValue: true },
        ],
      },
      noneValue: "none",
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: 23503, booleanAttributeValue: false },
          { attributeDefinitionId: 26744, booleanAttributeValue: false },
          { attributeDefinitionId: 23750, booleanAttributeValue: false },
          { attributeDefinitionId: 23518, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "recent_medications",
    type: QuestionType.CHECKBOX,
    question: "Have you recently taken any of the following medications?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "pcsk9_past_3mo",
        label: "I have taken PCSK9 inhibitors (Repatha, Praluent) within the past 3 months",
        disqualify: true,
      },
      {
        value: "gemfibrozil_current",
        label: "I am currently taking gemfibrozil (Lopid)",
        disqualify: true,
      },
      {
        value: "inclisiran_past_18mo",
        label: "I have taken inclisiran (Leqvio) within the past 18 months",
        disqualify: true,
      },
      {
        value: "mipomersen_past_12mo",
        label: "I have taken mipomersen (Kynamro) within the past 12 months",
        disqualify: true,
      },
      {
        value: "lomitapide_past_12mo",
        label: "I have taken lomitapide (Juxtapid) within the past 12 months",
        disqualify: true,
      },
      {
        value: "none",
        label: "None of the above is true for me",
      },
    ],
    disqualifyMessage: "This study requires a washout period from certain cholesterol medications.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        pcsk9_past_3mo: [
          { attributeDefinitionId: 22747, medicationGroupAttributeValue: { hasTaken: true, intervals: [] } },
        ],
        gemfibrozil_current: [
          { attributeDefinitionId: 22272, medicationAttributeValue: { hasTaken: true, intervals: [] } },
        ],
        inclisiran_past_18mo: [
          { attributeDefinitionId: 23568, medicationAttributeValue: { hasTaken: true, intervals: [] } },
        ],
        mipomersen_past_12mo: [
          { attributeDefinitionId: 26699, medicationAttributeValue: { hasTaken: true, intervals: [] } },
        ],
        lomitapide_past_12mo: [
          { attributeDefinitionId: 22806, medicationAttributeValue: { hasTaken: true, intervals: [] } },
        ],
      },
      noneValue: "none",
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: 22747, medicationGroupAttributeValue: { hasTaken: false } },
          { attributeDefinitionId: 22272, medicationAttributeValue: { hasTaken: false } },
          { attributeDefinitionId: 23568, medicationAttributeValue: { hasTaken: false } },
          { attributeDefinitionId: 26699, medicationAttributeValue: { hasTaken: false } },
          { attributeDefinitionId: 22806, medicationAttributeValue: { hasTaken: false } },
        ],
      },
    }),
  },

  {
    ...standardHeightQuestion,
    attributeMapping: mappers.height({
      attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT,
    }),
  },

  {
    ...standardWeightQuestion,
    attributeMapping: mappers.weight({
      attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT,
    }),
  },

  {
    id: "other_clinical_trials",
    type: QuestionType.RADIO,
    question: "Are you currently enrolling in or participating in any other clinical trials?",
    required: true,
    allowBack: true,
    options: [
      { value: "no", label: "No, I am not enrolling in or participating in any other clinical trials" },
      { value: "yes", label: "Yes, I am currently enrolling in or participating in another clinical trial", disqualify: true },
    ],
    disqualifyMessage: "Participants can only participate in one clinical trial at a time.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
      yes: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "inPersonAppointments",
    type: QuestionType.RADIO,
    question: "Can you attend regular in-person study visits over approximately 2-3 years?",
    helpText: "Your local trial site will be located within a reasonable distance from your home, with the exact address provided upon applying to the trial.",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes, I can attend in-person visits" },
      {
        value: "no",
        label: "No, I cannot attend in-person visits",
        disqualify: true,
      },
      {
        value: "discuss",
        label: "I would like to discuss the requirements with the research team",
      },
    ],
    disqualifyMessage: "This study requires regular in-person visits over a 2-3 year period.",
    attributeMapping: mappers.value({
      yes: [],
      no: [],
      discuss: [],
    }),
  },
];